import { useEffect, useState, useCallback } from 'react'
import { Activity, RefreshCw, GitBranch, AlertTriangle, Clock, Rocket } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Metric {
  value: number | null
  unit: string
  category: string
  raw_count?: number
  sev1_sev2_count?: number
  deploy_count?: number
  resolved_incident_count?: number
  note?: string
}

interface Dora {
  window_days: number
  metrics: {
    deploy_frequency: Metric
    change_failure_rate: Metric
    mttr: Metric
    lead_time: Metric
  }
  totals: { incidents: number; deploys: number }
}

const CAT_TONE: Record<string, string> = {
  Elite:  'bg-emerald/15 text-emerald ring-emerald/40',
  High:   'bg-blue/15 text-blue-light ring-blue/40',
  Medium: 'bg-amber/15 text-amber-light ring-amber/40',
  Low:    'bg-rose/15 text-rose ring-rose/40',
  '—':    'bg-surface-raised text-text-muted ring-border',
}

export default function DoraMetricsPage() {
  const [data, setData] = useState<Dora | null>(null)
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(90)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/eng/dora', { params: { window_days: days } })
      setData(r.data)
    } finally { setLoading(false) }
  }, [days])

  useEffect(() => { fetchData() }, [fetchData])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Activity size={24} className="text-blue-light" /> DORA Four Keys
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Computed from your incident log + watched GitHub releases. Categorised Elite / High / Medium / Low.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={e => setDays(parseInt(e.target.value, 10))} className="input py-1.5 text-xs">
            <option value={30}>30d</option><option value={60}>60d</option><option value={90}>90d</option><option value={180}>180d</option><option value={365}>365d</option>
          </select>
          <button type="button" onClick={fetchData} className="btn-ghost text-xs">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <KeyTile icon={Rocket} title="Deploy Frequency"
              value={data.metrics.deploy_frequency.value}
              unit="/ week"
              category={data.metrics.deploy_frequency.category}
              footnote={`${data.metrics.deploy_frequency.raw_count} GitHub releases observed in window`} />

            <KeyTile icon={Clock} title="Lead Time"
              value={data.metrics.lead_time.value}
              unit="hours"
              category={data.metrics.lead_time.category}
              footnote={data.metrics.lead_time.note || 'commit → production'} />

            <KeyTile icon={AlertTriangle} title="Change Failure Rate"
              value={data.metrics.change_failure_rate.value}
              unit="%"
              category={data.metrics.change_failure_rate.category}
              footnote={`${data.metrics.change_failure_rate.sev1_sev2_count} SEV1/SEV2 / ${data.metrics.change_failure_rate.deploy_count} deploys`} />

            <KeyTile icon={GitBranch} title="MTTR"
              value={data.metrics.mttr.value}
              unit="hours"
              category={data.metrics.mttr.category}
              footnote={`${data.metrics.mttr.resolved_incident_count} resolved incidents`} />
          </div>

          <div className="card p-3 text-[11px] text-text-secondary">
            <p>
              <strong>Source data:</strong> {data.totals.incidents} incidents · {data.totals.deploys} deploys over {data.window_days} days.
              MTTR uses <code className="font-mono">resolved_at − started_at</code> on the incidents table.
              Change-failure rate is a proxy: SEV1/SEV2 incidents ÷ deploys, capped at 100%.
              Lead-time requires the GH PR API (rate-budgeted) and is not yet enabled.
            </p>
          </div>
        </>
      )}
    </div>
  )
}

function KeyTile({ icon: Icon, title, value, unit, category, footnote }: {
  icon: React.ElementType
  title: string
  value: number | null
  unit: string
  category: string
  footnote: string
}) {
  const tone = CAT_TONE[category] || CAT_TONE['—']
  return (
    <div className={clsx('rounded-xl p-4 ring-2', tone)}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={12} />
        <h3 className="text-[10px] uppercase tracking-wider font-semibold">{title}</h3>
      </div>
      <div className="text-3xl font-black font-mono mt-1">
        {value == null ? '—' : value}
        {value != null && <span className="text-sm font-normal opacity-70 ml-1">{unit}</span>}
      </div>
      <div className="text-xs font-bold mt-1 uppercase">{category}</div>
      <p className="text-[9px] opacity-70 mt-2">{footnote}</p>
    </div>
  )
}
